import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Clock,
  Copy,
  Power,
  PowerOff,
  CalendarPlus,
  CalendarX2,
  Trash2,
  Loader2,
  Check,
  Sun,
  Moon,
  AlertTriangle,
  ChevronDown,
  Save,
} from 'lucide-react';
import {
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useBusinessContext } from '../../hooks/useBusinessContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { cn } from '../../lib/utils';
import type {
  WeeklyHours,
  DayOfWeek,
  DayHours,
  ScheduledClosure,
  ClosureReason,
} from '../../types/business';
import { DAYS_OF_WEEK } from '../../types/business';
import { formatTime12h } from '../../utils/time';

const DAY_LABELS: Record<DayOfWeek, string> = {
  monday: 'Mon',
  tuesday: 'Tue',
  wednesday: 'Wed',
  thursday: 'Thu',
  friday: 'Fri',
  saturday: 'Sat',
  sunday: 'Sun',
};

const DAY_LABELS_FULL: Record<DayOfWeek, string> = {
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
  sunday: 'Sunday',
};

const CLOSURE_REASONS: { value: ClosureReason; label: string; icon: string }[] = [
  { value: 'holiday', label: 'Holiday', icon: '🎉' },
  { value: 'event', label: 'Special Event', icon: '✨' },
  { value: 'vacation', label: 'Vacation', icon: '🏖️' },
  { value: 'maintenance', label: 'Maintenance', icon: '🔧' },
  { value: 'weather', label: 'Weather', icon: '🌧️' },
  { value: 'personal', label: 'Personal', icon: '🏠' },
  { value: 'other', label: 'Other', icon: '📝' },
];

function generateId(): string {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
}

export function StoreHoursPage() {
  const navigate = useNavigate();
  const { businessId, loading: contextLoading } = useBusinessContext();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const [hours, setHours] = useState<WeeklyHours>({
    monday: { open: '10:00', close: '18:00' },
    tuesday: { open: '10:00', close: '18:00' },
    wednesday: { open: '10:00', close: '18:00' },
    thursday: { open: '10:00', close: '18:00' },
    friday: { open: '10:00', close: '18:00' },
    saturday: { open: '11:00', close: '17:00' },
    sunday: null,
  });
  const [closedNow, setClosedNow] = useState(false);
  const [scheduledClosures, setScheduledClosures] = useState<ScheduledClosure[]>([]);
  const [businessName, setBusinessName] = useState('');

  const [showAddClosure, setShowAddClosure] = useState(false);
  const [newClosure, setNewClosure] = useState<{
    date: string;
    endDate: string;
    reason: ClosureReason;
    label: string;
    allDay: boolean;
    customOpen: string;
    customClose: string;
  }>({
    date: '',
    endDate: '',
    reason: 'holiday',
    label: '',
    allDay: true,
    customOpen: '10:00',
    customClose: '14:00',
  });

  useEffect(() => {
    if (contextLoading) return;
    if (!businessId) {
      setLoading(false);
      return;
    }

    async function loadBusiness() {
      try {
        const snap = await getDoc(doc(db, 'businesses', businessId!));
        if (snap.exists()) {
          const data = snap.data();
          setBusinessName(data.name || '');
          if (data.hours) setHours(data.hours);
          if (typeof data.closedNow === 'boolean') setClosedNow(data.closedNow);
          if (Array.isArray(data.scheduledClosures)) setScheduledClosures(data.scheduledClosures);
        }
      } catch (err) {
        console.error('Failed to load business hours:', err);
      } finally {
        setLoading(false);
      }
    }

    loadBusiness();
  }, [businessId, contextLoading]);

  const handleSave = useCallback(async () => {
    if (!businessId) return;
    setSaving(true);
    try {
      await updateDoc(doc(db, 'businesses', businessId), {
        hours,
        closedNow,
        scheduledClosures,
        updatedAt: serverTimestamp(),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      console.error('Failed to save hours:', err);
    } finally {
      setSaving(false);
    }
  }, [businessId, hours, closedNow, scheduledClosures]);

  const toggleClosedNow = useCallback(async () => {
    if (!businessId) return;
    const next = !closedNow;
    setClosedNow(next);
    try {
      await updateDoc(doc(db, 'businesses', businessId), {
        closedNow: next,
        active: !next,
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      console.error('Failed to toggle closed status:', err);
      setClosedNow(!next);
    }
  }, [businessId, closedNow]);

  const updateDayHours = (day: DayOfWeek, field: keyof DayHours, value: string) => {
    const current = hours[day];
    if (!current) return;
    setHours({ ...hours, [day]: { ...current, [field]: value } });
  };

  const toggleDay = (day: DayOfWeek) => {
    const current = hours[day];
    setHours({
      ...hours,
      [day]: current ? null : { open: '10:00', close: '18:00' },
    });
  };

  const copyToAll = (sourceDay: DayOfWeek) => {
    const sourceHours = hours[sourceDay];
    const newHours = { ...hours };
    DAYS_OF_WEEK.forEach(day => {
      if (day !== sourceDay && hours[day] !== null) {
        newHours[day] = sourceHours ? { ...sourceHours } : null;
      }
    });
    setHours(newHours);
  };

  const addClosure = () => {
    if (!newClosure.date || !newClosure.label) return;

    const closure: ScheduledClosure = {
      id: generateId(),
      date: newClosure.date,
      endDate: newClosure.endDate || undefined,
      reason: newClosure.reason,
      label: newClosure.label,
      allDay: newClosure.allDay,
      customOpen: newClosure.allDay ? undefined : newClosure.customOpen,
      customClose: newClosure.allDay ? undefined : newClosure.customClose,
      createdAt: new Date(),
    };

    setScheduledClosures(prev =>
      [...prev, closure].sort((a, b) => a.date.localeCompare(b.date))
    );
    setNewClosure({
      date: '',
      endDate: '',
      reason: 'holiday',
      label: '',
      allDay: true,
      customOpen: '10:00',
      customClose: '14:00',
    });
    setShowAddClosure(false);
  };

  const removeClosure = (id: string) => {
    setScheduledClosures(prev => prev.filter(c => c.id !== id));
  };

  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const upcomingClosures = scheduledClosures.filter(c => c.date >= today);
  const pastClosures = scheduledClosures.filter(c => c.date < today);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/business')}
                className="p-2 -ml-2 rounded-xl hover:bg-gray-100 transition-colors"
              >
                <ArrowLeft className="h-5 w-5 text-heading" />
              </button>
              <div>
                <h1 className="text-lg font-serif font-semibold text-heading">
                  Store Hours
                </h1>
                <p className="text-xs text-body">{businessName}</p>
              </div>
            </div>
            <Button
              size="sm"
              onClick={handleSave}
              loading={saving}
              disabled={saving}
              className={cn(saved && 'bg-green-600 hover:bg-green-600')}
            >
              {saved ? (
                <>
                  <Check className="h-4 w-4 mr-1.5" />
                  Saved
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-1.5" />
                  Save
                </>
              )}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* ── INSTANT CLOSE TOGGLE ── */}
        <Card className={cn(
          'overflow-hidden transition-all',
          closedNow && 'ring-2 ring-red-200 border-red-200'
        )}>
          <CardContent className="p-0">
            <button
              onClick={toggleClosedNow}
              className="w-full p-5 flex items-center justify-between group"
            >
              <div className="flex items-center gap-4">
                <div className={cn(
                  'w-12 h-12 rounded-2xl flex items-center justify-center transition-colors',
                  closedNow
                    ? 'bg-red-100 text-red-600'
                    : 'bg-green-100 text-green-600'
                )}>
                  {closedNow ? (
                    <PowerOff className="h-6 w-6" />
                  ) : (
                    <Power className="h-6 w-6" />
                  )}
                </div>
                <div className="text-left">
                  <p className="font-medium text-heading text-base">
                    {closedNow ? 'Store is Closed' : 'Store is Open'}
                  </p>
                  <p className="text-sm text-body mt-0.5">
                    {closedNow
                      ? 'Tap to reopen and accept orders'
                      : 'Tap to close for the rest of today'}
                  </p>
                </div>
              </div>
              <div className={cn(
                'w-14 h-8 rounded-full transition-colors relative flex-shrink-0',
                closedNow ? 'bg-red-500' : 'bg-green-500'
              )}>
                <div className={cn(
                  'w-6 h-6 bg-white rounded-full absolute top-1 transition-all shadow-md',
                  closedNow ? 'left-1' : 'left-7'
                )} />
              </div>
            </button>
            {closedNow && (
              <div className="px-5 pb-4 -mt-1">
                <div className="bg-red-50 border border-red-100 rounded-xl p-3 flex items-start gap-2.5">
                  <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-red-700">
                    Your store is not accepting orders. Shoppers will see your boutique as closed.
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* ── WEEKLY HOURS ── */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2.5">
              <Clock className="h-5 w-5 text-primary" />
              <CardTitle className="text-lg">Regular Hours</CardTitle>
            </div>
            <p className="text-sm text-body mt-1">
              Set your weekly operating schedule
            </p>
          </CardHeader>
          <CardContent className="space-y-2">
            {DAYS_OF_WEEK.map((day) => {
              const dayHours = hours[day];
              const isOpen = dayHours !== null;

              return (
                <div
                  key={day}
                  className={cn(
                    'flex items-center gap-3 p-3 rounded-xl border transition-all',
                    isOpen
                      ? 'border-gray-200 bg-white'
                      : 'border-gray-100 bg-gray-50/60'
                  )}
                >
                  <button
                    type="button"
                    onClick={() => toggleDay(day)}
                    className={cn(
                      'w-11 h-6 rounded-full transition-colors flex-shrink-0 relative',
                      isOpen ? 'bg-primary' : 'bg-gray-300'
                    )}
                  >
                    <div
                      className={cn(
                        'w-5 h-5 bg-white rounded-full absolute top-0.5 transition-all shadow-sm',
                        isOpen ? 'left-[22px]' : 'left-0.5'
                      )}
                    />
                  </button>

                  <span
                    className={cn(
                      'text-sm font-medium w-10 flex-shrink-0',
                      isOpen ? 'text-heading' : 'text-gray-400'
                    )}
                  >
                    {DAY_LABELS[day]}
                  </span>

                  {isOpen && dayHours ? (
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-1 min-w-0">
                        <Sun className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />
                        <input
                          type="time"
                          value={dayHours.open}
                          onChange={(e) => updateDayHours(day, 'open', e.target.value)}
                          className="h-9 rounded-lg border border-gray-200 px-2 text-sm font-sans focus:outline-none focus:ring-2 focus:ring-primary/30 w-full min-w-[100px]"
                        />
                        <span className="text-xs text-body whitespace-nowrap hidden sm:inline">
                          {formatTime12h(dayHours.open)}
                        </span>
                      </div>
                      <span className="text-body text-xs flex-shrink-0">to</span>
                      <div className="flex items-center gap-1.5 flex-1 min-w-0">
                        <Moon className="h-3.5 w-3.5 text-indigo-400 flex-shrink-0" />
                        <input
                          type="time"
                          value={dayHours.close}
                          onChange={(e) => updateDayHours(day, 'close', e.target.value)}
                          className="h-9 rounded-lg border border-gray-200 px-2 text-sm font-sans focus:outline-none focus:ring-2 focus:ring-primary/30 w-full min-w-[100px]"
                        />
                        <span className="text-xs text-body whitespace-nowrap hidden sm:inline">
                          {formatTime12h(dayHours.close)}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => copyToAll(day)}
                        className="p-1.5 rounded-lg text-primary hover:bg-primary/10 transition-colors flex-shrink-0"
                        title={`Copy ${DAY_LABELS_FULL[day]}'s hours to all open days`}
                      >
                        <Copy className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ) : (
                    <span className="text-sm text-gray-400 italic">Closed</span>
                  )}
                </div>
              );
            })}

            <div className="pt-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  const firstOpen = DAYS_OF_WEEK.find(d => hours[d] !== null);
                  if (firstOpen) copyToAll(firstOpen);
                }}
              >
                <Copy className="h-3.5 w-3.5 mr-1.5" />
                Same hours all days
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* ── SCHEDULED CLOSURES ── */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <CalendarX2 className="h-5 w-5 text-primary" />
                <CardTitle className="text-lg">Scheduled Closures</CardTitle>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowAddClosure(!showAddClosure)}
              >
                <CalendarPlus className="h-4 w-4 mr-1.5" />
                Add
              </Button>
            </div>
            <p className="text-sm text-body mt-1">
              Plan closures for holidays, events, or vacations
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Add Closure Form */}
            {showAddClosure && (
              <div className="bg-purple-50/50 border border-primary/15 rounded-2xl p-4 space-y-4 animate-in slide-in-from-top-2 duration-200">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-heading block mb-1.5">
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={newClosure.date}
                      min={today}
                      onChange={(e) => setNewClosure(p => ({ ...p, date: e.target.value }))}
                      className="w-full h-10 rounded-xl border border-gray-200 px-3 text-sm font-sans focus:outline-none focus:ring-2 focus:ring-primary/30"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-heading block mb-1.5">
                      End Date <span className="text-body font-normal">(optional)</span>
                    </label>
                    <input
                      type="date"
                      value={newClosure.endDate}
                      min={newClosure.date || today}
                      onChange={(e) => setNewClosure(p => ({ ...p, endDate: e.target.value }))}
                      className="w-full h-10 rounded-xl border border-gray-200 px-3 text-sm font-sans focus:outline-none focus:ring-2 focus:ring-primary/30"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-medium text-heading block mb-1.5">
                    Label
                  </label>
                  <input
                    type="text"
                    value={newClosure.label}
                    onChange={(e) => setNewClosure(p => ({ ...p, label: e.target.value }))}
                    placeholder="e.g. Memorial Day, Store Renovation"
                    className="w-full h-10 rounded-xl border border-gray-200 px-3 text-sm font-sans focus:outline-none focus:ring-2 focus:ring-primary/30 placeholder:text-gray-300"
                  />
                </div>

                <div>
                  <label className="text-xs font-medium text-heading block mb-1.5">
                    Reason
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {CLOSURE_REASONS.map((r) => (
                      <button
                        key={r.value}
                        type="button"
                        onClick={() => setNewClosure(p => ({ ...p, reason: r.value }))}
                        className={cn(
                          'px-3 py-1.5 rounded-full text-xs font-medium border transition-all',
                          newClosure.reason === r.value
                            ? 'bg-primary text-white border-primary'
                            : 'bg-white text-body border-gray-200 hover:border-primary/40'
                        )}
                      >
                        {r.icon} {r.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <button
                      type="button"
                      onClick={() => setNewClosure(p => ({ ...p, allDay: !p.allDay }))}
                      className={cn(
                        'w-11 h-6 rounded-full transition-colors relative',
                        !newClosure.allDay ? 'bg-primary' : 'bg-gray-300'
                      )}
                    >
                      <div
                        className={cn(
                          'w-5 h-5 bg-white rounded-full absolute top-0.5 transition-all shadow-sm',
                          !newClosure.allDay ? 'left-[22px]' : 'left-0.5'
                        )}
                      />
                    </button>
                    <span className="text-sm text-heading font-medium">
                      {newClosure.allDay ? 'Closed all day' : 'Reduced hours'}
                    </span>
                  </div>

                  {!newClosure.allDay && (
                    <div className="flex items-center gap-2 ml-14 animate-in slide-in-from-top-1 duration-150">
                      <div className="flex items-center gap-1.5">
                        <input
                          type="time"
                          value={newClosure.customOpen}
                          onChange={(e) => setNewClosure(p => ({ ...p, customOpen: e.target.value }))}
                          className="h-9 rounded-lg border border-gray-200 px-2 text-sm font-sans focus:outline-none focus:ring-2 focus:ring-primary/30"
                        />
                        <span className="text-xs text-body whitespace-nowrap">
                          {formatTime12h(newClosure.customOpen)}
                        </span>
                      </div>
                      <span className="text-body text-sm">to</span>
                      <div className="flex items-center gap-1.5">
                        <input
                          type="time"
                          value={newClosure.customClose}
                          onChange={(e) => setNewClosure(p => ({ ...p, customClose: e.target.value }))}
                          className="h-9 rounded-lg border border-gray-200 px-2 text-sm font-sans focus:outline-none focus:ring-2 focus:ring-primary/30"
                        />
                        <span className="text-xs text-body whitespace-nowrap">
                          {formatTime12h(newClosure.customClose)}
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex gap-2 pt-1">
                  <Button
                    size="sm"
                    onClick={addClosure}
                    disabled={!newClosure.date || !newClosure.label}
                  >
                    <CalendarPlus className="h-4 w-4 mr-1.5" />
                    Add Closure
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setShowAddClosure(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {/* Upcoming Closures */}
            {upcomingClosures.length > 0 ? (
              <div className="space-y-2">
                {upcomingClosures.map((closure) => (
                  <ClosureRow
                    key={closure.id}
                    closure={closure}
                    onRemove={() => removeClosure(closure.id)}
                  />
                ))}
              </div>
            ) : (
              !showAddClosure && (
                <div className="text-center py-8">
                  <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <CalendarX2 className="h-7 w-7 text-gray-400" />
                  </div>
                  <p className="text-sm font-medium text-heading">No upcoming closures</p>
                  <p className="text-xs text-body mt-1">
                    Plan ahead for holidays, events, or days off
                  </p>
                </div>
              )
            )}

            {/* Past Closures (collapsible) */}
            {pastClosures.length > 0 && (
              <PastClosuresSection closures={pastClosures} onRemove={removeClosure} />
            )}
          </CardContent>
        </Card>

        {/* Save bar (sticky bottom on mobile) */}
        <div className="sm:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 z-30">
          <Button
            fullWidth
            onClick={handleSave}
            loading={saving}
            disabled={saving}
            className={cn(saved && 'bg-green-600 hover:bg-green-600')}
          >
            {saved ? (
              <>
                <Check className="h-4 w-4 mr-1.5" />
                Changes Saved
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-1.5" />
                Save Changes
              </>
            )}
          </Button>
        </div>

        {/* Bottom spacer for mobile sticky bar */}
        <div className="h-20 sm:hidden" />
      </main>
    </div>
  );
}

function ClosureRow({
  closure,
  onRemove,
}: {
  closure: ScheduledClosure;
  onRemove: () => void;
}) {
  const reasonMeta = CLOSURE_REASONS.find(r => r.value === closure.reason);
  const startDate = new Date(closure.date + 'T00:00:00');
  const endDate = closure.endDate ? new Date(closure.endDate + 'T00:00:00') : null;

  const formatDate = (d: Date) =>
    d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  const isMultiDay = endDate && closure.endDate !== closure.date;

  return (
    <div className="flex items-center gap-3 p-3 rounded-xl border border-gray-200 bg-white group hover:border-primary/20 transition-colors">
      <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center text-lg flex-shrink-0">
        {reasonMeta?.icon || '📅'}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-heading truncate">{closure.label}</p>
        <p className="text-xs text-body mt-0.5">
          {formatDate(startDate)}
          {isMultiDay && endDate && ` — ${formatDate(endDate)}`}
          {!closure.allDay && closure.customOpen && closure.customClose && (
            <span className="text-primary ml-1.5">
              {formatTime12h(closure.customOpen)} – {formatTime12h(closure.customClose)}
            </span>
          )}
        </p>
      </div>
      <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-body flex-shrink-0 hidden sm:inline">
        {closure.allDay ? 'All day' : 'Reduced'}
      </span>
      <button
        onClick={onRemove}
        className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors opacity-0 group-hover:opacity-100 flex-shrink-0"
        title="Remove closure"
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  );
}

function PastClosuresSection({
  closures,
  onRemove,
}: {
  closures: ScheduledClosure[];
  onRemove: (id: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="border-t border-gray-100 pt-3">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 text-sm text-body hover:text-heading transition-colors w-full"
      >
        <ChevronDown
          className={cn(
            'h-4 w-4 transition-transform',
            expanded && 'rotate-180'
          )}
        />
        <span>{closures.length} past closure{closures.length !== 1 ? 's' : ''}</span>
      </button>
      {expanded && (
        <div className="space-y-2 mt-3 animate-in slide-in-from-top-2 duration-200">
          {closures.map((closure) => (
            <div key={closure.id} className="opacity-60">
              <ClosureRow closure={closure} onRemove={() => onRemove(closure.id)} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
